import LanguageGate from "@/components/LanguageGate";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Menu, Modal, UnstyledButton } from "@mantine/core";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { useSwipeable } from "react-swipeable";
import IconMapPin from "@/components/icons/IconMapPin";
import BrandIcon from "@/components/BrandIcon";
import BrandFooter from "@/components/BrandFooter";
import ReviewCard from "@/components/ReviewCard";
import { trackReviewClick } from "@/helpers/analytics";
import {
  buildLinkHref,
  isMapsHref,
  isTenantSite,
  linkLabel,
  opensInSameTab,
} from "@/helpers/branchLinks";
import { TENANT_LOGO_KIND } from "@/helpers/brandIcons";
import { pickContent } from "@/helpers/tenant";
import { t } from "@/i18n";
// DEFAULT_CATEGORY_IMAGE: görseli olmayan kategoriler için sabit fallback banner (eski menüdeki gibi).
import { DEFAULT_CATEGORY_IMAGE, loadingImageUrl } from "@/helpers/storage";

// Panel'in rich-text editöründen gelen HTML. html-react-parser sanitize etmez ve
// React <iframe srcdoc> gibi elemanları aynen basar — parse'tan önce temizlenir.
const renderRichText = (html) => {
  if (typeof html !== "string" || !html || html === "<p></p>") {
    return null;
  }
  return parse(DOMPurify.sanitize(html));
};

const MenuItem = memo(function MenuItem({
  product,
  lang,
  isFeatured,
  onPreview,
}) {
  const hasImage = !!product.imageUrl;
  const [loaded, setLoaded] = useState(!hasImage);
  const isLoading = hasImage && !loaded;

  const name = pickContent(lang, product, "name");
  const description = renderRichText(pickContent(lang, product, "description"));
  const open = () => onPreview(product.imageUrl, name);
  // Öne çıkan görünüm görseli TAM GENİŞLİK basıyor; "YENİ" rozetinin görsel üstünde durabildiği
  // tek yer burası. Normal satırda görsel 60px'lik bir kare ve rozet neredeyse yarısını kapatıyordu,
  // orada rozet adın önüne geçiyor.
  const featured = isFeatured && hasImage;

  return (
    <div className={`item-box ${featured ? "is-featured" : ""}`}>
      {hasImage && (
        <div className="item-thumb">
          <img
            className={`item-image swiper-thumb${isLoading ? " is-loading" : ""}`}
            src={product.imageUrl}
            alt={name}
            loading="lazy"
            role="button"
            tabIndex={0}
            aria-label={`${name} — ${t(lang, "product.expandImage")}`}
            style={{ cursor: "pointer" }}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            onClick={open}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                open();
              }
            }}
          />
          {product.isNew && featured && (
            <span className="item-new item-new--corner">
              {t(lang, "product.new")}
            </span>
          )}
        </div>
      )}
      <div className="item-content">
        <div className="primary-content">
          <div className="primary-head">
            <div className="primary-headtext">
              <h4
                className="primary-title w-image"
                style={{
                  marginBottom: description ? "10px" : "0px",
                  marginTop: "5px",
                }}
              >
                {product.isNew && !featured && (
                  <span className="item-new">{t(lang, "product.new")}</span>
                )}
                {name}
              </h4>
            </div>
            {product.price > 0 && (
              <>
                <div className="dots" />
                <div className="price-box">
                  {product.oldPrice > product.price && (
                    <div className="price-old">{product.oldPrice} ₺</div>
                  )}
                  <div
                    className={`price-content --item${
                      product.oldPrice > product.price ? " is-discounted" : ""
                    }`}
                  >
                    <div className="price">{product.price}</div>
                    <div className="currency">₺</div>
                  </div>
                </div>
              </>
            )}
          </div>
          {description}
        </div>
      </div>
    </div>
  );
});

// Varsayılan menü teması. Ortak veri (tenant/branch/categories/products) dispatcher'dan
// prop olarak gelir; favicon/GA/title/loading dispatcher'da kalır. Bu bileşen yalnızca
// görsel layout + etkileşim (dil, kategori gezinme, önizleme) barındırır. Yeni bir tema
// aynı props sözleşmesiyle eklenir. Kök .theme-default class'ı ileride tema-scope'lu CSS için.
export default function DefaultMenu({ data, branches, branchSlug }) {
  const categoryRefs = useRef({});
  const branchListRef = useRef(null);
  const activeBranchRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Diller API'den geliyor (Language tablosu + kiracının açtıkları), menüde sabit liste YOK.
  // Boş dizi savunma amaçlı: yanıt gelmeden bir render turu oluyor ve seçici o turda çizilmemeli.
  const languages = data?.languages ?? [];
  // Seçili dil KOD olarak tutuluyor ve taban dil varsayılan. Liste veriden geldiği için
  // başlangıç değeri de veriden: eskiden 'TR' sabitti ve tabanı Türkçe olmayan bir kiracıda
  // menü yanlış dille açılırdı.
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const currentLanguage =
    languages.find((item) => item.code === selectedLanguage) ??
    languages.find((item) => item.isBase) ??
    languages[0] ??
    null;
  const lang = currentLanguage?.code ?? "tr";

  // Sağdan sola yazılan diller: belgenin dir'ini güncelle (metin yönü + CSS'teki [dir='rtl']
  // kuralları). Yön dilin KAYDINDAN geliyor; eskiden `=== 'AR'` sabitiydi ve İbranice/Farsça
  // eklense soldan sağa basılırdı.
  useEffect(() => {
    document.documentElement.dir = currentLanguage?.direction ?? "ltr";
    document.documentElement.lang = lang ?? "tr";
    return () => {
      document.documentElement.dir = "ltr";
    };
  }, [currentLanguage?.direction, lang]);
  const [previewImage, setPreviewImage] = useState(null);

  const categories = data?.categories ?? [];
  const tenant = data?.tenant;
  // Açılış dil seçimi panelden kapatılabiliyor. Anahtar YOKSA açık sayılıyor: ayar eklenmeden
  // önceki menülerin davranışı buydu ve sessizce değiştirmek canlı menüleri değiştirmek olurdu.
  const languageGate = tenant?.menuSettings?.languageGate !== false;
  const needsLanguage =
    languageGate && languages.length > 1 && !selectedLanguage;
  const branch = data?.branch;

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }

    const activeRef = categoryRefs.current[selectedCategory.id];
    if (activeRef) {
      activeRef.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [selectedCategory]);

  // Şubelerimiz yatay listesinde aktif şube taşmışsa görünür değil — container'ı ona kaydır
  // (sadece listeyi, sayfayı değil).
  useEffect(() => {
    const list = branchListRef.current;
    const active = activeBranchRef.current;
    if (!list || !active) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    list.scrollLeft +=
      activeRect.left -
      listRect.left -
      (list.clientWidth - active.clientWidth) / 2;
  }, [branchSlug, branches]);

  // delta: +1 sonraki kategori, -1 önceki. Sola kaydırma ileri gider.
  const stepCategory = (delta) => {
    const currentIndex = categories.findIndex(
      (category) => category.id === selectedCategory.id,
    );
    const nextCategory = categories[currentIndex + delta];
    if (nextCategory) {
      setSelectedCategory(nextCategory);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => stepCategory(1),
    onSwipedRight: () => stepCategory(-1),
  });

  // memo'lu MenuItem'ın her render'da yeniden render olmaması için referans sabit.
  const showPreview = useCallback(
    (src, alt) => setPreviewImage({ src, alt }),
    [],
  );

  // Her biri kendi başına gizlenebiliyor; üçü birden kapalıyken place-content boş kalır ama
  // borderTop'u yüzünden logonun altında anlamsız gri bir şerit çizerdi.
  const showPriceDate = Boolean(
    branch?.settings?.showPriceDate && branch?.settings?.priceUpdatedAt,
  );
  const showLinks =
    Boolean(branch?.links?.length) && branch?.settings?.showLinks !== false;
  // Tek şubede de gösteriliyor: bu blok bir şube SEÇİCİ değil, adres ve yol tarifi bağlantısı.
  // `> 1` iken tek şubeli kiracıda menüde hiç konum bilgisi kalmıyordu.
  const showBranches =
    branches.length > 0 && branch?.settings?.showBranches !== false;
  // Değerlendirme bağlantısı header'ın üstünde, bu bloğun dışında — showPlaceInfo'ya girmiyor.
  const showReview = Boolean(branch?.settings?.reviewUrl);
  const showPlaceInfo = showPriceDate || showLinks || showBranches;

  // Contact ikonları: harita HARİÇ tüm açık bağlantılar. Harita dışlanıyor çünkü "Şubelerimiz"
  // listesinde her şube zaten kendi haritasına bağlanıyor.
  //
  // Filtre eskiden `LINK_KINDS[link.icon]` istiyordu, yani anahtarın bilinen bir tür olmasını.
  // Anahtar serbest emojiye dönünce o koşul HİÇBİR bağlantıyı geçirmez oldu — iletişim satırı
  // sessizce boşalıyordu.
  const contactLinks = (branch?.links ?? []).filter(
    (link) =>
      link.active !== false &&
      !isMapsHref(buildLinkHref(link.icon, link.value)),
  );

  if (needsLanguage) {
    return (
      <LanguageGate
        languages={languages}
        tenant={tenant}
        onSelect={setSelectedLanguage}
      />
    );
  }

  return (
    <div className="body-element view-menu theme-default">
      <div className="place">
        <div className="place-body">
          <div
            className="place-header wrapper"
            onClick={() => setSelectedCategory(null)}
          >
            {/* Tek dil varsa seçici hiç çizilmiyor: tıklayınca hiçbir şey değişmeyen
                            bir bayrak, kiracı bir dil açtı sanılmasına yol açıyordu. */}
            {languages.length > 1 && (
              <div
                className="lang-switch"
                onClick={(event) => event.stopPropagation()}
              >
                <Menu>
                  <Menu.Target>
                    <UnstyledButton
                      className="menu-item-flag-button"
                      aria-label={t(lang, "language.select")}
                    >
                      <span className="lang-flag">{currentLanguage?.flag}</span>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown style={{ backgroundColor: "whitesmoke" }}>
                    {languages.map((item) => (
                      <Menu.Item
                        key={item.code}
                        className="menu-item-flag"
                        aria-label={item.label}
                        onClick={() => setSelectedLanguage(item.code)}
                      >
                        {/* Yalnızca bayrak — dil adı YAZILMIYOR. Seçici
                                                    menü başlığının üstünde küçük bir kutu; ad
                                                    eklendiğinde açılır liste genişleyip menüyü
                                                    örtüyordu. Ad `aria-label`'da duruyor, ekran
                                                    okuyucu okuyor. */}
                        <span className="lang-flag">{item.flag}</span>
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </div>
            )}
            {/* Üst orta: kenardaki dikey sekme gözden kaçıyordu, rozet artık logonun
                            üst bandında duruyor. Header'ın kendi tıklaması kategori seçimini
                            sıfırlıyor (bkz. onClick), bağlantı onu tetiklemesin. */}
            {showReview && (
              <div
                className="review-slot"
                onClick={(event) => event.stopPropagation()}
              >
                <ReviewCard
                  lang={lang}
                  url={branch?.settings?.reviewUrl}
                  onOpen={() => trackReviewClick(branch?.slug)}
                />
              </div>
            )}
            {tenant?.logoUrl ? (
              <div
                className="place-header__bg"
                style={{
                  backgroundImage: `url("${tenant.logoUrl}")`,
                  backgroundSize: "contain",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <div className="place-header__bg place-header__bg--text">
                <h1 style={{ textAlign: "center", paddingTop: "30%" }}>
                  {tenant?.name}
                </h1>
              </div>
            )}
          </div>

          {showPlaceInfo && (
            <div
              className="place-content wrapper"
              style={{
                borderTop: "1px solid gray",
                marginTop: "1px",
              }}
            >
              <div className="place-info" style={{ color: "black" }}>
                {showPriceDate && (
                  <div className="place-info__date">
                    {t(lang, "price.updatedAt")}:{" "}
                    {branch.settings.priceUpdatedAt
                      .split("-")
                      .reverse()
                      .join(".")}
                  </div>
                )}

                {showLinks && (
                  <div
                    className={`place-info__contact${contactLinks.length >= 8 ? " is-split" : ""}`}
                  >
                    {contactLinks.map((link, index) => {
                      const href = buildLinkHref(link.icon, link.value);
                      const label = linkLabel(href, link.icon);
                      // Kiracının kendi sitesine giden bağlantı, seçilen simge
                      // ne olursa olsun markanın kendi amblemiyle çizilsin.
                      const iconKind = isTenantSite(href, tenant?.slug)
                        ? TENANT_LOGO_KIND
                        : link.icon;
                      return (
                        <a
                          key={index}
                          className="contact-icon"
                          href={href}
                          target={opensInSameTab(href) ? undefined : "_blank"}
                          rel="noreferrer"
                          aria-label={label}
                        >
                          <BrandIcon
                            kind={iconKind}
                            markUrl={tenant?.markUrl}
                            logoUrl={tenant?.logoUrl}
                            loadingImageUrl={loadingImageUrl(tenant?.slug)}
                          />
                        </a>
                      );
                    })}
                  </div>
                )}

                {showBranches && (
                  <div className="place-info__branches">
                    <div className="place-info__branches-label">
                      {t(lang, "branches.title")}
                    </div>
                    <div
                      className="place-info__branch-list"
                      ref={branchListRef}
                    >
                      {branches.map((item) => {
                        // Harita bağlantısı ADRESTEN bulunuyor: anahtar
                        // artık serbest emoji, 'maps' diye bir tür yok.
                        const maps = (item.links ?? [])
                          .filter((link) => link.active !== false)
                          .map((link) => buildLinkHref(link.icon, link.value))
                          .find(isMapsHref);
                        const active =
                          item.slug === branchSlug ? " active" : "";

                        if (!maps) {
                          return (
                            <span
                              key={item.slug}
                              ref={active ? activeBranchRef : undefined}
                              className={`place-info__branch${active}`}
                              style={{ cursor: "default" }}
                            >
                              <IconMapPin />
                              <span>{item.name}</span>
                            </span>
                          );
                        }

                        return (
                          <a
                            key={item.slug}
                            ref={active ? activeBranchRef : undefined}
                            className={`place-info__branch${active}`}
                            href={maps}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`${item.name} — ${t(lang, "branches.map")}`}
                          >
                            <IconMapPin />
                            <span>{item.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="menu-list mb-3 right-fade category-nav">
        {selectedCategory && (
          <div className="ozel-menu">
            <div className="header-menu">
              <div
                className="bottom"
                style={{
                  gap: "10px",
                }}
                onWheel={(e) => {
                  e.currentTarget.scrollLeft += e.deltaY;
                }}
              >
                {categories.map((category) => (
                  <UnstyledButton
                    ref={(el) => (categoryRefs.current[category.id] = el)}
                    className={`menu-itm ${category.id === selectedCategory.id ? "active" : ""}`}
                    key={category.id}
                    aria-current={
                      category.id === selectedCategory.id ? "true" : undefined
                    }
                    onClick={() => setSelectedCategory(category)}
                  >
                    <div className="menu-nm">
                      {pickContent(lang, category, "name")}
                    </div>
                  </UnstyledButton>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {!selectedCategory && (
        <div className="sections-content">
          <div id="menu-content" className="menu-grid">
            {categories.map((category) => (
              <UnstyledButton
                className={`menu-grid-item style-${category.view ?? 2}`}
                key={category.id}
                style={{
                  // Görseli olmayan kategori için sabit fallback banner; o da 404
                  // olursa alttaki koyu renk görünür.
                  backgroundImage: `url(${category.imageUrl || DEFAULT_CATEGORY_IMAGE})`,
                  backgroundColor: "#2b2b2b",
                }}
                onClick={() => setSelectedCategory(category)}
              >
                <div className="black-box" />
                <span className="text-box">
                  {pickContent(lang, category, "name")}
                </span>
              </UnstyledButton>
            ))}
          </div>
        </div>
      )}

      {selectedCategory && (
        <div className="items-container" {...handlers}>
          {(selectedCategory.products ?? []).map((product) => (
            <MenuItem
              key={product.id}
              product={product}
              lang={lang}
              isFeatured={selectedCategory.isFeatured}
              onPreview={showPreview}
            />
          ))}
        </div>
      )}

      <Modal
        opened={!!previewImage}
        onClose={() => setPreviewImage(null)}
        centered
        withCloseButton={false}
        padding={0}
        size="auto"
        zIndex={10000}
        styles={{
          content: { backgroundColor: "transparent", boxShadow: "none" },
          body: { display: "flex", justifyContent: "center" },
        }}
      >
        {previewImage && (
          <img
            src={previewImage.src}
            alt={previewImage.alt}
            onClick={() => setPreviewImage(null)}
            style={{
              width: "min(85vw, 380px)",
              height: "min(85vw, 380px)",
              maxHeight: "85vh",
              objectFit: "cover",
              borderRadius: "var(--dm-rounded-s, 8px)",
              cursor: "pointer",
            }}
          />
        )}
      </Modal>

      <footer>
        <BrandFooter />
      </footer>
    </div>
  );
}
